import * as Yup from 'yup';

import { EMAIL_PATTERN } from '@core/constants/common';

import { ALLOWED_ATTACHMENT_EXTENSIONS_PATTERN } from '../constants/app';
import type { EmailRecipient } from '../types/api/EmailRecipient/EmailRecipient';
import type {
  SendMessageInput,
  SendMessageInputWireFormat,
} from '../types/api/SendMessageInput/SendMessageInput';
import type { UploadFileInput } from '../types/api/UploadFileInput/UploadFileInput';
import type { Base64 } from '../utils/base64';
import type { ExcludeMethods } from '../utils/types';
import { YupBase64, YupOptionalString, YupPDFFileName, YupString } from '../utils/yupHelpers';

export type EmailFormikType = Pick<SendMessageInputWireFormat, 'body' | 'subject'> & {
  // EmailRecipient must be explicit because its sub-property `name` is optional,
  // and `DomainObject<string | undefined>` does not work with Yup.
  bcc: EmailRecipient[];
  cc: EmailRecipient[];
  from: EmailRecipient[];
  to: EmailRecipient[];
  file: AttachmentFileFormikEntry;
  files: AttachmentFileFormikEntry[];
};

export const isValidEmailAddress = (email: string): boolean => EMAIL_PATTERN.test(email);

/**
 * EmailRecipientSchema mimics the yup shape of `EmailRecipient` with the caveat
 * that email is only required, rather than of email format.
 *
 * When using this schema in an array, it is recommended to be used in conjunction with `YupEmail`
 * to hoist the error from the array object level to the higher level input name.
 */
const EmailRecipientSchema: Yup.SchemaOf<EmailRecipient> = Yup.object().shape({
  // YupEmail is not used because it would generate errors at the array sub-object level.
  email: Yup.string().label('Email address').default(''),
  name: YupOptionalString('Name'),
});

const EmailRecipientListSchema: Yup.SchemaOf<EmailRecipient[]> = Yup.array()
  .of(EmailRecipientSchema)
  .test({
    name: 'is-valid-email',
    message: 'All inputs must be valid email addresses',
    test: (v) => (v ?? []).every((i) => isValidEmailAddress(i.email ?? '')),
  });

/** Array-level test (not `.of(YupEmail())`) so the error lands on the field, not on `field[0]`. */
export const EmailListSchema: Yup.SchemaOf<string[]> = Yup.array()
  .of(Yup.string().default(''))
  .test({
    name: 'is-valid-email',
    message: 'All inputs must be valid email addresses',
    test: (v) => (v ?? []).every(isValidEmailAddress),
  });

export const FileSchema: Yup.SchemaOf<ExcludeMethods<UploadFileInput>> = Yup.object().shape({
  fileName: YupPDFFileName('File name'),
  file: YupBase64('base64 data of file'),
});

/**
 * AttachmentFileFormikEntry is the shape of a single additional attachment
 * held in Formik state. The `file` field holds the base64-encoded content
 * read from the user-selected File object.
 */
export type AttachmentFileFormikEntry = {
  fileName: string;
  file: Base64;
};

export const AttachmentFileSchema: Yup.SchemaOf<AttachmentFileFormikEntry> = Yup.object().shape({
  fileName: YupString('File name').matches(
    ALLOWED_ATTACHMENT_EXTENSIONS_PATTERN,
    'Only PDF, Word, Excel, image (JPG/PNG), and text files are supported.',
  ),
  file: YupBase64('file content'),
});

export const EmailSchema: Yup.SchemaOf<EmailFormikType> = Yup.object().shape({
  body: YupString('Body'),
  subject: YupString('Subject'),
  to: EmailRecipientListSchema.min(1).label('To'),
  from: EmailRecipientListSchema.label('From'),
  cc: EmailRecipientListSchema.label('CC'),
  bcc: EmailRecipientListSchema.label('BCC'),
  file: FileSchema,
  files: Yup.array().of(AttachmentFileSchema).default([]),
});

export const FormikEmail = (input: Partial<SendMessageInput> | undefined): EmailFormikType => ({
  body: input?.body ?? '',
  subject: input?.subject ?? '',
  to:
    input?.to?.map((i) => ({
      email: i.email ?? '',
      name: i.name,
    })) ?? [],
  from:
    input?.from?.map((i) => ({
      email: i.email ?? '',
      name: i.name,
    })) ?? [],
  cc:
    input?.cc?.map((i) => ({
      email: i.email ?? '',
      name: i.name,
    })) ?? [],
  bcc:
    input?.bcc?.map((i) => ({
      email: i.email ?? '',
      name: i.name,
    })) ?? [],
  file: {
    file: input?.file?.file ?? '',
    fileName: input?.file?.fileName ?? 'quote.pdf',
  },
  files: [],
});
